import { useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PageShell } from '@/components/layout/PageShell';
import { HottrMark } from '@/components/brand/HottrMark';
import { SectionMarker } from '@/components/brand/SectionMarker';
import { PinkDot } from '@/components/brand/PinkDot';
import { ROUTES } from '@/config/routes';
import { env } from '@/config/env';
import { getSupabase } from '@/lib/supabase';
import {
  completePassportLogin,
  consumePassportReturnTo,
  isPassportEnabled,
  PASSPORT_AUTH_STORAGE_KEY,
  type PassportExchangeResult,
} from '@/auth/passport';

const SIGNIN_TIMEOUT_MS = 25_000;

/**
 * One-time SSO codes + OTPs must not run twice (React StrictMode remounts
 * effects, and Safari can double-fire). Cache the full handoff by auth code
 * so every mount shares one in-flight pipeline.
 */
const handoffCache = new Map<string, Promise<void>>();

function completePassportHandoffOnce(
  code: string,
  state: string,
): Promise<void> {
  const existing = handoffCache.get(code);
  if (existing) return existing;
  const p = (async () => {
    const result = await completePassportLogin(code, state);
    await mintAppSession(result);
  })().catch((err) => {
    handoffCache.delete(code);
    throw err;
  });
  handoffCache.set(code, p);
  return p;
}

async function mintAppSession(result: PassportExchangeResult): Promise<void> {
  const dest = result.destination;
  if (dest?.email_otp && dest.project_url) {
    if (!env.supabaseAnonKey) {
      throw new Error(
        'Missing VITE_SUPABASE_ANON_KEY — cannot complete sign-in.',
      );
    }
    const main = getSupabase();
    const destUrl = dest.project_url.replace(/\/$/, '');
    const ourUrl = (env.supabaseUrl || '').replace(/\/$/, '');
    const client =
      main && destUrl === ourUrl
        ? main
        : createClient(dest.project_url, env.supabaseAnonKey, {
            auth: {
              persistSession: true,
              autoRefreshToken: true,
              detectSessionInUrl: false,
              flowType: 'pkce',
              storageKey: 'hottr.app.auth',
            },
          });

    const { error: otpErr } = await client.auth.verifyOtp({
      email: dest.email,
      token: dest.email_otp,
      type: 'email',
    });
    if (otpErr) throw otpErr;

    if (
      result.passport?.email_otp &&
      result.passport.anon_key &&
      !result.passport.anon_key.includes('*')
    ) {
      const p = createClient(
        result.passport.project_url,
        result.passport.anon_key,
        {
          auth: {
            persistSession: true,
            autoRefreshToken: true,
            detectSessionInUrl: false,
            storageKey: PASSPORT_AUTH_STORAGE_KEY,
          },
        },
      );
      await p.auth
        .verifyOtp({
          email: result.passport.email,
          token: result.passport.email_otp,
          type: 'email',
        })
        .catch(() => {
          /* dest session is enough for app */
        });
    }
    return;
  }

  if (
    result.passport?.email_otp &&
    result.passport.anon_key &&
    !result.passport.anon_key.includes('*')
  ) {
    const p = createClient(
      result.passport.project_url,
      result.passport.anon_key,
      {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: false,
          storageKey: PASSPORT_AUTH_STORAGE_KEY,
        },
      },
    );
    const { error: pErr } = await p.auth.verifyOtp({
      email: result.passport.email,
      token: result.passport.email_otp,
      type: 'email',
    });
    if (pErr) throw pErr;
    return;
  }

  throw new Error(
    'Sign-in completed, but this app couldn’t start a session — try again.',
  );
}

/**
 * Auth callback:
 * 1. Passport SSO (?code=&state=) — exchange PKCE, mint destination session
 * 2. Legacy Supabase stored session
 */
export const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('Signing in');

  const code = params.get('code') || '';
  const state = params.get('state') || '';

  useEffect(() => {
    let alive = true;
    const timeoutId = window.setTimeout(() => {
      if (alive) {
        setError(
          'Sign-in is taking too long. Close this tab and try again.',
        );
      }
    }, SIGNIN_TIMEOUT_MS);

    const finish = (path: string) => {
      if (!alive) return;
      window.clearTimeout(timeoutId);
      navigate(path.startsWith('/') ? path : ROUTES.profileBuilder, {
        replace: true,
      });
    };

    const fail = (msg: string) => {
      if (!alive) return;
      window.clearTimeout(timeoutId);
      if (/code_used|invalid_code|code_expired|Invalid SSO state/i.test(msg)) {
        setError(
          'That sign-in link was already used or expired. Go back and try again.',
        );
      } else {
        setError(msg);
      }
    };

    const run = async () => {
      // If a previous mount already minted a session, just go in.
      const existing = getSupabase();
      if (existing) {
        const { data } = await existing.auth.getSession();
        if (data.session && !code) {
          finish(ROUTES.profileBuilder);
          return;
        }
        // If we already have a session and we're retrying a burnt code, prefer session
        if (data.session && code) {
          finish(consumePassportReturnTo() || ROUTES.profileBuilder);
          return;
        }
      }

      if (code && isPassportEnabled()) {
        try {
          if (alive) setStatus('Confirming sign-in');
          // Strip query so nothing else consumes Passport's one-time code.
          if (window.history.replaceState) {
            window.history.replaceState({}, '', window.location.pathname);
          }

          if (alive) setStatus('Opening your session');
          await completePassportHandoffOnce(code, state);
          finish(consumePassportReturnTo() || ROUTES.profileBuilder);
          return;
        } catch (e) {
          // Race: first mount may have minted session before this failed
          const sb = getSupabase();
          if (sb) {
            const { data } = await sb.auth.getSession();
            if (data.session) {
              finish(consumePassportReturnTo() || ROUTES.profileBuilder);
              return;
            }
          }
          fail(e instanceof Error ? e.message : 'Sign-in failed');
          return;
        }
      }

      const supabase = getSupabase();
      if (!supabase) {
        finish(ROUTES.landing);
        return;
      }
      await new Promise((r) => setTimeout(r, 60));
      const { data } = await supabase.auth.getSession();
      if (data.session) finish(ROUTES.profileBuilder);
      else finish(ROUTES.landing);
    };

    void run();
    return () => {
      alive = false;
      window.clearTimeout(timeoutId);
    };
  }, [code, state, navigate]);

  return (
    <PageShell bare>
      <main className="flex min-h-dvh items-center justify-center bg-ink px-6 text-center">
        <div className="space-y-6">
          <HottrMark size="text-2xl" surface=".app" />
          {error ? (
            <div className="mx-auto max-w-sm space-y-4">
              <SectionMarker className="justify-center">Sign-in failed</SectionMarker>
              <p className="font-display text-sm font-bold uppercase tracking-wider text-pink">
                {error}
              </p>
              <Link
                to={ROUTES.landing}
                className="inline-block font-display text-[11px] font-bold uppercase tracking-widest text-text-secondary hover:text-pink"
              >
                Back to start
              </Link>
            </div>
          ) : (
            <>
              <SectionMarker className="justify-center">{status}</SectionMarker>
              <p className="font-display text-3xl font-black uppercase tracking-display">
                Holding the door<span className="text-pink">.</span>
              </p>
              <div className="flex justify-center">
                <PinkDot pulse size="h-2 w-2" />
              </div>
            </>
          )}
        </div>
      </main>
    </PageShell>
  );
};
